import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TrendLine from '../../components/charts/TrendLine';
import { formatCelsius, formatDelta } from '../../utils/thermal/legend';
import { EXPERIMENT_KEY, UNIT_ROIS, buildSeries, hasUnitRois, lidStates, roiLabels } from '../../utils/thermal/series';
import { box, input, label as labelStyle } from '../shared/styles';

/*
 * The time series of the Thermal & Metabolic tab (docs/FIELD_AI_VISION_SPEC.md
 * section 43): one ROI label's mean temperature, or its difference from the
 * reference, over capture time -- a line per unit, or per arm (the mean of its
 * replicates per day, with their range). The values are the server's ROI
 * statistics (utils/thermal/series.js). A point opens its capture. The
 * caption names the lid state of the points: a closed lid is the lid's own
 * temperature, not the culture's.
 *
 * When ROIs were saved as measuring units (plan phase 10), "Each unit's own
 * region" comes first: every unit's line from the ROI tied to it, whatever its
 * label (utils/thermal/series.js UNIT_ROIS); a label's line puts such an ROI
 * on its own unit only.
 */
export default function ThermalSeries({ captures, units, template, onOpen }) {
  const { t } = useTranslation(['experiments', 'thermal']);
  const labels = useMemo(() => {
    const seen = roiLabels(captures);
    const first = (template?.rois || []).map((r) => r.label).filter((l) => seen.some((s) => s.toLowerCase() === l.toLowerCase()));
    const own = hasUnitRois(captures) ? [UNIT_ROIS] : [];
    return [...own, ...first, ...seen.filter((s) => !first.some((f) => f.toLowerCase() === s.toLowerCase()))];
  }, [captures, template]);
  const [chosen, setChosen] = useState(null);
  const [metric, setMetric] = useState('t_avg');
  const [by, setBy] = useState('unit');
  const roi = chosen && labels.includes(chosen) ? chosen : labels[0];
  const series = useMemo(() => (roi ? buildSeries(captures, units, { label: roi, metric, by }) : [])
    .map((s) => ({ ...s, name: s.key === EXPERIMENT_KEY ? t('thermal.series.wholeExperiment') : s.name })),
  [captures, units, roi, metric, by, t]);
  const format = metric === 'delta' ? (v) => formatDelta(v, 1) : (v) => formatCelsius(v, 1);
  const states = lidStates(series);

  if (!labels.length) return null;
  return (
    <section style={box} aria-labelledby="thermal-series-title" data-testid="thermal-series">
      <h4 id="thermal-series-title" style={{ marginTop: 0 }}>{t('thermal.series.title')}</h4>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <label style={labelStyle}>{t('thermal.series.roi')}
          <select style={input} value={roi} onChange={(e) => setChosen(e.target.value)} data-testid="thermal-series-roi">
            {labels.map((l) => <option key={l} value={l}>{l === UNIT_ROIS ? t('thermal.series.unitRois') : l}</option>)}
          </select>
        </label>
        <label style={labelStyle}>{t('thermal.series.metric')}
          <select style={input} value={metric} onChange={(e) => setMetric(e.target.value)} data-testid="thermal-series-metric">
            <option value="t_avg">{t('thermal.series.metric_t_avg')}</option>
            <option value="delta">{t('thermal.series.metric_delta')}</option>
          </select>
        </label>
        <label style={labelStyle}>{t('thermal.series.by')}
          <select style={input} value={by} onChange={(e) => setBy(e.target.value)} data-testid="thermal-series-by">
            <option value="unit">{t('thermal.series.by_unit')}</option>
            <option value="arm">{t('thermal.series.by_arm')}</option>
          </select>
        </label>
      </div>
      <TrendLine series={series} testId="thermal-trend"
        yLabel={metric === 'delta' ? t('thermal.series.axisDelta') : t('thermal.series.axis')}
        formatValue={(v) => (metric === 'delta' ? v.toFixed(1) : v.toFixed(1))}
        emptyMessage={t('thermal.series.empty')}
        onPoint={(p) => onOpen?.(p.captureIds[0])}
        pointLabel={(p, s) => t('thermal.series.point', { name: s.name, when: new Date(p.t).toISOString().slice(0, 10),
          value: format(p.value), n: p.n })} />
      {series.length > 0 && (
        <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', margin: '4px 0 0' }} data-testid="thermal-series-caption">
          {t('thermal.series.caption', { states: states.map((s) => t(`thermal:lid.${s}`)).join(', ') })}
          {states.includes('closed') ? ` ${t('thermal:lid.closedWarning')}` : ''}
        </p>
      )}
    </section>
  );
}
